import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCubeQuery } from '@cubejs-client/react';
import { useSnackbar } from 'notistack';

import { CAMPAIGNS_TRACKING_LINKS } from 'config/routes';
import {
    ITrackingLink,
    TRACKING_LINK_CLICKS_MEASURE,
    TRACKING_LINK_SHORT_LINK_ID_DIMENSION
} from '../trackingLinks';
import { linksApi } from '../trackingLinksApi';

export const useTrackingLinksView = ({
    linkId
}: {
    linkId: string;
}): { trackingLink: ITrackingLink | null; clicks: number; isClicksLoading: boolean } => {
    const [trackingLink, setTrackingLink] = React.useState<ITrackingLink>(null);
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    React.useEffect(() => {
        const getLink = async () => {
            const { body, ok, statusCode } = await linksApi.get(linkId);
            if (statusCode === 404) {
                enqueueSnackbar('That campaign link may have been deleted. Please try again later', {
                    variant: 'error'
                });
                navigate(CAMPAIGNS_TRACKING_LINKS);
                return;
            }
            if (!ok) {
                throw new Error(body.message);
            }
            setTrackingLink(body);
        };
        getLink();
    }, [linkId, enqueueSnackbar, navigate]);

    const clicksQuery = useCubeQuery(
        {
            measures: [TRACKING_LINK_CLICKS_MEASURE],
            dimensions: [TRACKING_LINK_SHORT_LINK_ID_DIMENSION],
            filters: [
                {
                    member: TRACKING_LINK_SHORT_LINK_ID_DIMENSION,
                    operator: 'equals',
                    values: trackingLink ? [trackingLink.shortLinkId] : []
                }
            ]
        },
        { skip: !trackingLink?.shortLinkId }
    );

    const clicks = React.useMemo(() => {
        const rows = clicksQuery.resultSet?.tablePivot() ?? [];
        const row = rows[0];
        return row ? Number(row[TRACKING_LINK_CLICKS_MEASURE] ?? 0) : 0;
    }, [clicksQuery.resultSet]);

    return { trackingLink, clicks, isClicksLoading: clicksQuery.isLoading };
};
